import { useCallback, useMemo, useRef, useState } from 'react'
import { FiHelpCircle } from 'react-icons/fi'
import { useDispatch } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import { Form } from '@unform/web'
import { t } from 'i18next'
import moment from 'moment'

import { ButtonsCancelSave } from '~/components/Buttons'
import { DefinitionItem } from '~/components/DefinitionGrid'
import { LogoStore } from '~/components/LogoStore'
import Panel from '~/components/Panel'
import Status from '~/components/Status'
import Tooltip from '~/components/Tooltip'
import { Checkbox } from '~/components/unform'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import {
  formatDateToStringIgnoreUTC,
  getAccountType,
  getDurationInDays,
  joinWithCommasAndAnd
} from '~/helpers'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'
import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '~/modules/retailMedia/services/configuration/campaign-permissions'
import { useAppSelector } from '~/store/hooks'

import {
  getLabelAdType,
  getLabelChannel
} from '../../dtos/common/campaign/format'
import { useCampaignReadOnly } from '../../hooks/useCampaignReadOnly'
import { useDestinationPublisherNames } from '../../hooks/useDestinationPublisherNames'
import { patch } from '../../store/industry/campaign/actions'
import EditButton from '../EditButton'
import ReactivateButton from '../ReactivateButton'

import * as S from './styles'

type CampaignInfoViewFormData = FormNameAndTimeIntervalData & {
  settings?: {
    age_restricted?: boolean
  }
}

export const InfoView = ({ data }: { data: IndustryCampaignView }) => {
  const {
    industry: {
      publisherConfiguration: {
        list: { data: configurationData }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const [isEditMode, setIsEditMode] = useState(false)

  const formRef = useRef(null)

  const dispatch = useDispatch()

  // Só o fluxo offsite tem varejista de destino. Passar `undefined` fora dele
  // mantém a query desabilitada, em vez de buscar a lista de publishers à toa
  // em toda campanha onsite.
  /**
   * `type`, e não `ad_type`: o primeiro é o contexto da campanha, o segundo o
   * formato do criativo. É o mesmo critério do recorte da fila de revisão
   * (`services/campaign/offsite-advertiser-review`), e por lá a razão está
   * escrita: filtrar pelo formato perde campanha no dia em que existir um
   * segundo formato offsite. Hoje as duas perguntas dão a mesma resposta, já
   * que qualquer offsite é `banner_off_site`, então isto é a mesma leitura por
   * um campo que não caduca.
   */
  const isOffsite = data?.type === 'off_site'

  const { names: destinationRetailerNames } = useDestinationPublisherNames(
    isOffsite ? data?.raw?.destination_publishers : undefined
  )

  const showDestinationRetailer = isOffsite && !!destinationRetailerNames.length

  const loadingSave = false
  const publisherId = useMemo(
    () => data?.publisher_id || data?.publisher?.id || data?.raw?.publisher_id,
    [data]
  )
  const { data: campaignPermissions } = useQuery({
    enabled: !!publisherId,
    queryKey: [getCampaignPermissionsServiceKey, { publisher_id: publisherId }],
    queryFn: async () => {
      const response = await getCampaignPermissionsService({
        publisher_id: publisherId
      })

      return response.data
    }
  })

  const isGrocery = getAccountType() === 'grocery'

  const isReadOnlyView = useCampaignReadOnly()

  const canEdit = useMemo(
    () => data?.canEditCampaign && !isReadOnlyView,
    [data?.canEditCampaign, isReadOnlyView]
  )

  const startDateIsPast = useMemo(
    () => moment().utc().isSameOrAfter(data?.raw?.start_at, 'day'),
    [data]
  )

  const duration = useMemo(() => {
    if (!data?.raw?.end_at) return null

    return getDurationInDays({
      startDate: data?.raw?.start_at,
      endDate: data?.raw?.end_at
    })
  }, [data?.raw?.end_at, data?.raw?.start_at])

  const initialData: CampaignInfoViewFormData = useMemo(
    () => ({
      name: data?.title,
      description: data?.description,
      start_date: formatDateToStringIgnoreUTC(
        data?.raw?.start_at,
        'YYYY-MM-DD'
      ),
      end_date: data?.raw?.end_at
        ? formatDateToStringIgnoreUTC(data?.raw?.end_at, 'YYYY-MM-DD')
        : null,
      hasFinalDate: !data?.raw?.end_at,
      settings: {
        age_restricted: !!data?.raw?.settings?.age_restricted
      },
      duration
    }),
    [data, duration]
  )

  /**
   * Handle submit
   */

  const handleSubmit = useCallback(
    (formData: CampaignInfoViewFormData) => {
      const body = {
        start_at: formData.start_date,
        end_at: formData?.end_date || null,
        name: formData.name,
        description: formData?.description,
        settings: {
          age_restricted: !!formData?.settings?.age_restricted
        }
      }

      dispatch(
        patch({
          body,
          id: data?.id,
          successFeedbackMsg: t('rm:campaign.successfully.updated'),
          onSuccess: () => setIsEditMode(false)
        })
      )
    },
    [data, dispatch]
  )

  /** Handle brands */
  const publisherBrands = useMemo(
    () => configurationData?.configuration?.brands,
    [configurationData]
  )

  const allowAgeRestrictedCampaign = useMemo(
    () => campaignPermissions?.allow_age_restricted_campaign,
    [campaignPermissions?.allow_age_restricted_campaign]
  )

  /** Reactivate campaign */
  const shouldShowReactivateButton = useMemo(() => {
    return (
      data?.status === 'finished' &&
      data?.adType !== 'digital_signage' &&
      !isReadOnlyView
    )
  }, [data?.status, data?.adType, isReadOnlyView])

  return (
    <Panel
      title={t('rm:generalInformation')}
      template={isEditMode ? 'borderWarning' : 'shoreline'}
      headerSideContent={
        !isGrocery && (
          <>
            {canEdit && (
              <EditButton
                onClick={() => setIsEditMode((e: boolean) => !e)}
                isActive={isEditMode}
              />
            )}

            {shouldShowReactivateButton && (
              <ReactivateButton
                onClick={() => setIsEditMode((e: boolean) => !e)}
                isActive={isEditMode}
              />
            )}
          </>
        )
      }
    >
      {/* Ordem dos campos como o desenho do offsite a define — `.review-info-grid`
          em `apps/offsite/v2.html` —, com os campos que só existem aqui
          encostados nos seus parentes: tipo junto do canal, duração junto das
          datas. */}
      <S.Grid>
        <DefinitionItem label={t('rm:campaignTitle')}>
          {data?.title}
        </DefinitionItem>

        {/* Anunciante e publisher **juntos**, e não um no lugar do outro
            conforme o tipo de conta: numa revisão de briefing, de quem é a
            campanha é o ponto. Cada lado aparece quando tem nome. */}
        {!!data?.advertiser?.name && (
          <DefinitionItem label={t('rm:Advertiser')}>
            <S.Party>
              <LogoStore
                backgroundLogo={data?.advertiser?.account_theme?.header_color}
                alt={data?.advertiser?.name}
                linkLogo={data?.advertiser?.logo}
              />
              {data.advertiser.name}
            </S.Party>
          </DefinitionItem>
        )}

        <DefinitionItem label={t('rm:campaignType')}>
          {getLabelAdType(data?.adType)}
        </DefinitionItem>

        {isOffsite && (
          <DefinitionItem label={t('rm:campaign.offsite.channel.title')}>
            {getLabelChannel(data?.raw?.settings?.channel)}
          </DefinitionItem>
        )}

        {!isEditMode && (
          <>
            <DefinitionItem label="Status">
              <Status status={campaignStatusBadge(data?.status)} />
            </DefinitionItem>

            <DefinitionItem label={t('rm:startDate')}>
              <span className="dateLabel">
                {data?.startDateWithHours || t('rm:startDate')}
              </span>
            </DefinitionItem>

            <DefinitionItem label={t('rm:endDate')}>
              <span className="dateLabel">
                {data?.endDateWithHours || t('rm:noEndDateDefined')}
              </span>
            </DefinitionItem>

            {!!data?.duration && (
              <DefinitionItem label={t('rm:duration')}>
                {data?.duration}
                {data?.remaining && ` - ${data?.remaining}`}
              </DefinitionItem>
            )}
          </>
        )}

        {!!data?.publisher?.name && (
          <DefinitionItem label="Publisher">
            <S.Party>
              <LogoStore
                backgroundLogo={data?.publisher?.account_theme?.header_color}
                alt={data?.publisher?.name}
                linkLogo={data?.publisher?.logo}
              />
              {data.publisher.name}
            </S.Party>
          </DefinitionItem>
        )}

        {/* A campanha só devolve ids em `destination_publishers`; o nome é
            resolvido contra a lista de publishers onsite. Sem nome resolvido,
            o campo não aparece — em vez de mostrar um id cru. */}
        {showDestinationRetailer && (
          <DefinitionItem
            label={t('rm:campaign.offsite.destinationRetailer.title')}
          >
            {joinWithCommasAndAnd({ values: destinationRetailerNames })}
          </DefinitionItem>
        )}

        {/* Precisa ter um loading melhor pra essa seção para não usar validação direta no dado */}
        {!!publisherBrands?.length && !!data?.publisher?.name && (
          <DefinitionItem
            label={
              <S.Term>
                {t('rm:adTags')}{' '}
                <Tooltip
                  text={t('rm:adTags.tooltip.description', {
                    name: data.publisher.name
                  })}
                  icon={<FiHelpCircle />}
                />
              </S.Term>
            }
          >
            {joinWithCommasAndAnd({
              values: publisherBrands.map(({ name }) => name)
            })}
          </DefinitionItem>
        )}

        {!isEditMode && (
          <>
            {allowAgeRestrictedCampaign && (
              <DefinitionItem
                label={t('rm:campaign.settings.ageRestricted.title')}
              >
                {data?.raw?.settings?.age_restricted
                  ? t('rm:campaign.settings.ageRestricted.value.yes')
                  : t('rm:campaign.settings.ageRestricted.value.no')}
              </DefinitionItem>
            )}

            {!!data?.description && (
              <DefinitionItem label={t('rm:description')} wide>
                {data.description}
              </DefinitionItem>
            )}
          </>
        )}
      </S.Grid>

      {isEditMode && (
        <S.RowWrapper className="hasBorder">
          <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
            <NameAndTimeInterval
              formRef={formRef}
              labelTitle={t(
                'rm:campaign.create.NameAndTimeInterval.labelTitle'
              )}
              placeholderTitle={t(
                'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
              )}
              hasDescription
              labelDescription={t(
                'rm:campaign.create.NameAndTimeInterval.labelDescription'
              )}
              placeholderDescription={t(
                'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
              )}
              labelInterval={t(
                'rm:campaign.create.NameAndTimeInterval.labelInterval'
              )}
              onlyDates
              endDateIsRequired={false}
              showValidBadgeOnSection
              intervalAlertMessage={[
                t('rm:campaign.create.NameAndTimeInterval.intervalAlertMessage')
              ]}
              showEndDateOptional
              containerElement="section"
              isEditMode
              startDateReadOnly={startDateIsPast}
              initialData={initialData}
              autoFocus={data.status === 'finished' ? 'end_date' : 'name'}
              canEditDate={data?.adType !== 'digital_signage'}
            />

            {allowAgeRestrictedCampaign && (
              <S.EditSectionWrapper>
                <S.GroupWrapper>
                  <S.Wrapper>
                    <strong>
                      {t('rm:campaign.settings.ageRestricted.title')}
                    </strong>
                    <Checkbox
                      name="settings.age_restricted"
                      label={t('rm:campaign.settings.ageRestricted.label')}
                    />
                  </S.Wrapper>
                </S.GroupWrapper>
              </S.EditSectionWrapper>
            )}

            <ButtonsCancelSave
              onCancelClick={() => setIsEditMode(false)}
              isLoading={loadingSave}
              showSavingChangesLoading
            />
          </Form>
        </S.RowWrapper>
      )}
    </Panel>
  )
}
